import React, { useState } from "react";
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import {
  FaCommentDots,
  FaCheckCircle,
  FaExclamationTriangle,
  FaClipboardCheck,
  FaUsers,
  FaBell,
  FaChevronRight,
  FaPlus,
  FaMinus,
} from "react-icons/fa";
import { MdHowToVote } from "react-icons/md";

import "../../Styles/PoliticalSurveyWarRoom.css";

function PoliticalSurveyWarRoom() {
  const [ageGenderTab, setAgeGenderTab] = useState("Age Group");

  // ---------- STAT CARDS ----------
  const statCards = [
    {
      icon: <FaCommentDots />,
      label: "TOTAL RESPONSES",
      value: "1,25,480",
      change: "+8.4% vs yesterday",
      color: "politicalwar123-ic-green",
    },
    {
      icon: <FaCheckCircle />,
      label: "COMPLETION %",
      value: "68.7%",
      change: "+5.6% vs yesterday",
      color: "politicalwar123-ic-blue",
    },
    {
      icon: <FaExclamationTriangle />,
      label: "TOP ISSUES",
      value: "Roads, Water, Employment",
      link: "View details",
      color: "politicalwar123-ic-orange",
      small: true,
    },
    {
      icon: <FaClipboardCheck />,
      label: "PENDING VERIFICATION",
      value: "3,248",
      link: "View items",
      color: "politicalwar123-ic-purple",
    },
    {
      icon: <FaUsers />,
      label: "FIELD TEAMS ACTIVE",
      value: "256",
      link: "View all teams",
      color: "politicalwar123-ic-teal",
    },
  ];

  // ---------- SURVEY RESPONSE TRENDS ----------
  const trendData = [
    { time: "6 AM", today: 2000, yesterday: 1800 },
    { time: "9 AM", today: 8500, yesterday: 7200 },
    { time: "12 PM", today: 14500, yesterday: 12800 },
    { time: "3 PM", today: 21450, yesterday: 18200 },
    { time: "6 PM", today: 27800, yesterday: 24500 },
    { time: "9 PM", today: 34200, yesterday: 30100 },
  ];

  // ---------- TOP PUBLIC ISSUES DONUT ----------
  const issuesData = [
    { name: "Roads & Infrastructure", value: 32.6, color: "#16a34a" },
    { name: "Drinking Water", value: 21.8, color: "#3b82f6" },
    { name: "Employment", value: 17.4, color: "#f59e0b" },
    { name: "Healthcare", value: 12.6, color: "#8b5cf6" },
    { name: "Welfare Schemes", value: 8.7, color: "#06b6d4" },
    { name: "Others", value: 6.9, color: "#9ca3af" },
  ];

  // ---------- EMERGING CONCERNS ----------
  const emergingConcerns = [
    { issue: "Drinking Water", change: "+18.6%", trend: [4, 6, 5, 8, 10, 9, 12] },
    { issue: "Roads & Infrastructure", change: "+12.3%", trend: [5, 5, 7, 6, 9, 8, 10] },
    { issue: "Unemployment", change: "+9.8%", trend: [3, 4, 6, 5, 7, 8, 9] },
    { issue: "Healthcare Services", change: "+6.7%", trend: [6, 5, 6, 7, 6, 8, 7] },
    { issue: "Waste Management", change: "+4.1%", trend: [4, 5, 4, 5, 6, 5, 6] },
  ];

  // ---------- DEMOGRAPHIC INSIGHTS ----------
  const demographicData = [
    { label: "18-25 Years", value: 18.7, color: "#16a34a" },
    { label: "26-35 Years", value: 28.6, color: "#3b82f6" },
    { label: "36-45 Years", value: 24.3, color: "#f59e0b" },
    { label: "46-60 Years", value: 19.5, color: "#8b5cf6" },
    { label: "60+ Years", value: 8.9, color: "#6b7280" },
  ];

  // ---------- LIVE SURVEY FEED ----------
  const liveFeed = [
    {
      time: "10:28 AM",
      village: "Kondapur Village, Kondapur Mandal",
      text: "People facing irregular drinking water supply for the last 20 days.",
      surveyor: "Ramesh",
      tag: "Drinking Water",
      tagColor: "politicalwar123-tag-blue",
      isNew: true,
    },
    {
      time: "10:27 AM",
      village: "Pedda Cheriapalli, Chintalapudi Mandal",
      text: "Road is damaged badly, leading to traffic and transportation issues.",
      surveyor: "Sreenu",
      tag: "Roads",
      tagColor: "politicalwar123-tag-green",
      isNew: true,
    },
    {
      time: "10:26 AM",
      village: "Mogalthur Village, Yerragondapalem Mandal",
      text: "Youth are looking for job opportunities and skill training programs.",
      surveyor: "Kavitha",
      tag: "Employment",
      tagColor: "politicalwar123-tag-orange",
      isNew: true,
    },
  ];

  // ---------- FIELD TEAM PERFORMANCE ----------
  const fieldTeams = [
    { name: "Team Alpha", mandal: "Kondapur", assigned: 1250, completed: 982, rate: "78.6%", quality: "Good" },
    { name: "Team Beta", mandal: "Chintalapudi", assigned: 1180, completed: 980, rate: "83.1%", quality: "Good" },
    { name: "Team Gamma", mandal: "Yerragondapalem", assigned: 980, completed: 672, rate: "68.6%", quality: "Good" },
    { name: "Team Delta", mandal: "Velpuru", assigned: 850, completed: 598, rate: "70.4%", quality: "Good" },
    { name: "Team Epsilon", mandal: "Ippatam", assigned: 760, completed: 492, rate: "64.7%", quality: "Average" },
  ];

  // ---------- ACTION ITEMS ----------
  const actionItems = [
    { title: "Drinking Water Issue in Kondapur Mandal", date: "25 May 2025", priority: "High Priority", priorityColor: "politicalwar123-priority-high" },
    { title: "Road Repair Request, Pedda Cheriapalli", date: "26 May 2025", priority: "Medium Priority", priorityColor: "politicalwar123-priority-medium" },
    { title: "Employment & Skill Training Programs", date: "27 May 2025", priority: "Medium Priority", priorityColor: "politicalwar123-priority-medium" },
    { title: "Healthcare Camp Requirement", date: "28 May 2025", priority: "Low Priority", priorityColor: "politicalwar123-priority-low" },
  ];

  // ---------- SURVEY QUALITY ----------
  const qualityData = [
    { name: "Valid Responses", value: 92, count: "1,15,562", color: "#16a34a" },
    { name: "Partial Responses", value: 6, count: "7,842", color: "#f59e0b" },
    { name: "Low Quality", value: 2, count: "2,076", color: "#ef4444" },
  ];

  // ---------- MOCK CONSTITUENCY MAP GRID ----------
  const mapDensities = ["high", "medium", "low", "verylow"];
  const mapGrid = Array.from({ length: 70 }, (_, i) => {
    const rand = Math.random();
    let density = "medium";
    if (rand < 0.3) density = "high";
    else if (rand < 0.55) density = "medium";
    else if (rand < 0.8) density = "low";
    else density = "verylow";
    return density;
  });

  return (
    <div className="politicalwar123-page">
      {/* ================= HEADER ================= */}
      <div className="politicalwar123-header">
        <div className="politicalwar123-header-left">
          <h1 className="politicalwar123-title">
            POLITICAL <span className="politicalwar123-title-accent">SURVEY WARROOM</span>
          </h1>
          <p className="politicalwar123-subtitle">
            Real-time survey intelligence and field-feedback command center
          </p>
        </div>

        <div className="politicalwar123-header-right">
          {/* <div className="politicalwar123-datetime">
            <span className="politicalwar123-time">10:30 AM</span>
            <span className="politicalwar123-date">May 24, 2025</span>
          </div> */}

          {/* <div className="politicalwar123-bell-wrap">
            <FaBell className="politicalwar123-bell-icon" />
            <span className="politicalwar123-bell-badge">12</span>
          </div> */}

          {/* <div className="politicalwar123-admin-box">
            <div className="politicalwar123-admin-avatar">CA</div>
            <div className="politicalwar123-admin-info">
              <span className="politicalwar123-admin-name">Campaign Admin</span>
              <span className="politicalwar123-admin-role">Super Admin</span>
            </div>
          </div> */}
        </div>
      </div>

      {/* ================= STAT CARDS ================= */}
      <div className="politicalwar123-stats-grid">
        {statCards.map((s, i) => (
          <div className="politicalwar123-stat-card" key={i}>
            <div className={`politicalwar123-stat-icon ${s.color}`}>{s.icon}</div>
            <div className="politicalwar123-stat-info">
              <span className="politicalwar123-stat-label">{s.label}</span>
              <span
                className={`politicalwar123-stat-value ${
                  s.small ? "politicalwar123-stat-value-small" : ""
                }`}
              >
                {s.value}
              </span>
              {s.change && (
                <span className="politicalwar123-stat-change">{s.change}</span>
              )}
              {s.link && (
                <a href="#!" className="politicalwar123-stat-link">
                  {s.link} <FaChevronRight />
                </a>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* ================= ROW 1: TRENDS / ISSUES / MAP ================= */}
      <div className="politicalwar123-row-3">
        {/* Survey Response Trends */}
        <div className="politicalwar123-card">
          <div className="politicalwar123-card-header">
            <h3>Survey Response Trends</h3>
          </div>

          <div className="politicalwar123-legend-row">
            <span className="politicalwar123-legend-item">
              <span className="politicalwar123-legend-dot politicalwar123-dot-green" /> Today
            </span>
            <span className="politicalwar123-legend-item politicalwar123-legend-dashed">
              <span className="politicalwar123-legend-dot politicalwar123-dot-grey" /> Yesterday
            </span>
          </div>

          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={trendData}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="time" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Line
                type="monotone"
                dataKey="today"
                stroke="#16a34a"
                strokeWidth={2}
                dot={{ r: 3 }}
              />
              <Line
                type="monotone"
                dataKey="yesterday"
                stroke="#9ca3af"
                strokeWidth={1.5}
                strokeDasharray="4 4"
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Top Public Issues */}
        <div className="politicalwar123-card">
          <div className="politicalwar123-card-header">
            <h3>Top Public Issues</h3>
          </div>

          <div className="politicalwar123-donut-wrap">
            <div className="politicalwar123-donut-chart-box">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={issuesData}
                    dataKey="value"
                    innerRadius={55}
                    outerRadius={80}
                    paddingAngle={2}
                  >
                    {issuesData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="politicalwar123-donut-legend">
              {issuesData.map((d, i) => (
                <div className="politicalwar123-legend-line" key={i}>
                  <span
                    className="politicalwar123-legend-dot"
                    style={{ background: d.color }}
                  />
                  <span className="politicalwar123-legend-label">{d.name}</span>
                  <span className="politicalwar123-legend-value">{d.value}%</span>
                </div>
              ))}
            </div>
          </div>

          <a href="#!" className="politicalwar123-viewall-link">
            View all issues <FaChevronRight />
          </a>
        </div>

        {/* Survey on Map */}
        <div className="politicalwar123-card">
          <div className="politicalwar123-card-header">
            <h3>Survey on Map</h3>
          </div>

          <div className="politicalwar123-map-filters">
            <select className="politicalwar123-map-select">
              <option>Constituency: All</option>
            </select>
            <select className="politicalwar123-map-select">
              <option>Mandal: All</option>
            </select>
            <select className="politicalwar123-map-select">
              <option>Village / Area: All</option>
            </select>
          </div>

          <div className="politicalwar123-map-box">
            <div className="politicalwar123-map-grid">
              {mapGrid.map((density, i) => (
                <span
                  key={i}
                  className={`politicalwar123-map-cell politicalwar123-density-${density}`}
                />
              ))}
            </div>

            <div className="politicalwar123-map-controls">
              <button className="politicalwar123-map-btn">
                <FaPlus />
              </button>
              <button className="politicalwar123-map-btn">
                <FaMinus />
              </button>
            </div>
          </div>

          <div className="politicalwar123-map-legend">
            <span className="politicalwar123-map-legend-title">Response Density</span>
            <div className="politicalwar123-map-legend-row">
              <span className="politicalwar123-map-legend-dot politicalwar123-density-high" />
              High
            </div>
            <div className="politicalwar123-map-legend-row">
              <span className="politicalwar123-map-legend-dot politicalwar123-density-medium" />
              Medium
            </div>
            <div className="politicalwar123-map-legend-row">
              <span className="politicalwar123-map-legend-dot politicalwar123-density-low" />
              Low
            </div>
            <div className="politicalwar123-map-legend-row">
              <span className="politicalwar123-map-legend-dot politicalwar123-density-verylow" />
              Very Low
            </div>
          </div>
        </div>
      </div>

      {/* ================= ROW 2: EMERGING CONCERNS / DEMOGRAPHICS / LIVE FEED ================= */}
      <div className="politicalwar123-row-3">
        {/* Emerging Concerns */}
        <div className="politicalwar123-card">
          <div className="politicalwar123-card-header">
            <h3>Emerging Concerns (Last 7 Days)</h3>
          </div>

          <table className="politicalwar123-mini-table">
            <thead>
              <tr>
                <th>Issue</th>
                <th>Change</th>
                <th>Trend</th>
              </tr>
            </thead>
            <tbody>
              {emergingConcerns.map((c, i) => (
                <tr key={i}>
                  <td>{c.issue}</td>
                  <td className="politicalwar123-change-cell">{c.change}</td>
                  <td>
                    <svg width="70" height="24" viewBox="0 0 70 24">
                      <polyline
                        fill="none"
                        stroke="#16a34a"
                        strokeWidth="2"
                        points={c.trend
                          .map(
                            (v, idx) =>
                              `${idx * 12},${24 - (v / Math.max(...c.trend)) * 20}`
                          )
                          .join(" ")}
                      />
                    </svg>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Demographic Insights */}
        <div className="politicalwar123-card">
          <div className="politicalwar123-card-header">
            <h3>Demographic Insights (Aggregated)</h3>
          </div>

          <div className="politicalwar123-tabs-row">
            {["Age Group", "Gender", "Community", "Education"].map((tab) => (
              <button
                key={tab}
                className={`politicalwar123-tab-btn ${
                  ageGenderTab === tab ? "politicalwar123-tab-active" : ""
                }`}
                onClick={() => setAgeGenderTab(tab)}
              >
                {tab}
              </button>
            ))}
          </div>

          <div className="politicalwar123-demo-list">
            {demographicData.map((d, i) => (
              <div className="politicalwar123-demo-row" key={i}>
                <span className="politicalwar123-demo-label">{d.label}</span>
                <div className="politicalwar123-demo-track">
                  <div
                    className="politicalwar123-demo-fill"
                    style={{ width: `${d.value * 2}%`, background: d.color }}
                  />
                </div>
                <span className="politicalwar123-demo-value">{d.value}%</span>
              </div>
            ))}
          </div>

          <div className="politicalwar123-demo-axis">
            <span>0%</span>
            <span>10%</span>
            <span>20%</span>
            <span>30%</span>
          </div>
        </div>

        {/* Live Survey Feed */}
        <div className="politicalwar123-card">
          <div className="politicalwar123-card-header">
            <h3>Live Survey Feed</h3>
            <a href="#!" className="politicalwar123-viewall-link">
              View All
            </a>
          </div>

          <div className="politicalwar123-feed-list">
            {liveFeed.map((f, i) => (
              <div className="politicalwar123-feed-item" key={i}>
                <div className="politicalwar123-feed-top">
                  <span className="politicalwar123-feed-time">{f.time}</span>
                  <span className={`politicalwar123-feed-tag ${f.tagColor}`}>
                    {f.tag}
                  </span>
                  {f.isNew && (
                    <span className="politicalwar123-feed-new">New</span>
                  )}
                </div>
                <span className="politicalwar123-feed-village">{f.village}</span>
                <p className="politicalwar123-feed-text">{f.text}</p>
                <span className="politicalwar123-feed-surveyor">
                  Surveyor: {f.surveyor}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ================= ROW 3: FIELD TEAM / ACTION ITEMS / SURVEY QUALITY ================= */}
      <div className="politicalwar123-row-3">
        {/* Field Team Performance */}
        <div className="politicalwar123-card politicalwar123-card-wide">
          <div className="politicalwar123-card-header">
            <h3>Field-Team Performance</h3>
          </div>

          <div className="politicalwar123-table-wrap">
            <table className="politicalwar123-field-table">
              <thead>
                <tr>
                  <th>Team Name</th>
                  <th>Mandal</th>
                  <th>Surveys Assigned</th>
                  <th>Completed</th>
                  <th>Completion %</th>
                  <th>Quality Score</th>
                </tr>
              </thead>
              <tbody>
                {fieldTeams.map((t, i) => (
                  <tr key={i}>
                    <td>{t.name}</td>
                    <td>{t.mandal}</td>
                    <td>{t.assigned.toLocaleString()}</td>
                    <td>{t.completed.toLocaleString()}</td>
                    <td>{t.rate}</td>
                    <td>
                      <span
                        className={`politicalwar123-quality-badge ${
                          t.quality === "Good"
                            ? "politicalwar123-quality-good"
                            : "politicalwar123-quality-average"
                        }`}
                      >
                        {t.quality}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Action Items */}
        <div className="politicalwar123-card">
          <div className="politicalwar123-card-header">
            <h3>Action Items</h3>
            <a href="#!" className="politicalwar123-viewall-link">
              View All
            </a>
          </div>

          <div className="politicalwar123-action-list">
            {actionItems.map((a, i) => (
              <div className="politicalwar123-action-item" key={i}>
                <span className="politicalwar123-action-num">{i + 1}</span>
                <div className="politicalwar123-action-info">
                  <span className="politicalwar123-action-title">{a.title}</span>
                  <span className="politicalwar123-action-date">{a.date}</span>
                </div>
                <span className={`politicalwar123-priority-badge ${a.priorityColor}`}>
                  {a.priority}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Survey Quality */}
        <div className="politicalwar123-card">
          <div className="politicalwar123-card-header">
            <h3>Survey Quality</h3>
          </div>

          <div className="politicalwar123-quality-wrap">
            <div className="politicalwar123-quality-chart-box">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={qualityData}
                    dataKey="value"
                    innerRadius={55}
                    outerRadius={78}
                    startAngle={90}
                    endAngle={-270}
                  >
                    {qualityData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="politicalwar123-quality-center">
                <span className="politicalwar123-quality-percent">92%</span>
                <span className="politicalwar123-quality-sub">Good Quality</span>
              </div>
            </div>

            <div className="politicalwar123-quality-legend">
              {qualityData.map((q, i) => (
                <div className="politicalwar123-quality-legend-row" key={i}>
                  <span
                    className="politicalwar123-legend-dot"
                    style={{ background: q.color }}
                  />
                  <span className="politicalwar123-quality-legend-label">
                    {q.name}
                  </span>
                  <span className="politicalwar123-quality-legend-pct">
                    ({q.value}%)
                  </span>
                </div>
              ))}
              <div className="politicalwar123-quality-total-row">
                <span>Total</span>
                <span>1,25,480 (100%)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PoliticalSurveyWarRoom;